import React, { useState, useEffect } from 'react';
import { API, useAuth } from '../../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { Users, IndianRupee, TrendingUp, Ticket, Brain, Star, Target, Clock } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_LABELS = {
  student_complaint: 'Student Complaint', teacher_complaint: 'Teacher Complaint',
  payment_issue: 'Payment Issue', technical_issue: 'Technical Issue',
  academic_issue: 'Academic Issue', general_query: 'General Query',
};
const PIE_COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#0ea5e9'];

const KpiCard = ({ icon: Icon, label, value, color, suffix }) => (
  <Card className="border-0 shadow-md">
    <CardContent className="p-5 flex items-center justify-between">
      <div>
        <p className="text-xs font-medium text-slate-500">{label}</p>
        <p className={`text-2xl font-bold ${color}`}>{value}{suffix}</p>
      </div>
      <div className={`w-10 h-10 rounded-lg ${color.replace('text', 'bg').replace('600', '100')} flex items-center justify-center`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
    </CardContent>
  </Card>
);

const AnalyticsDashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/analytics/dashboard`)
      .then((r) => setData(r.data))
      .catch(() => toast.error('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-20 text-slate-600">Loading analytics...</div>;
  if (!data) return null;

  const k = data.kpis;
  const categoryData = Object.entries(data.complaints.by_category || {}).map(([key, value]) => ({ name: CATEGORY_LABELS[key] || key, value }));
  const sisData = Object.entries(data.sis.distribution || {}).map(([range, count]) => ({ range, count }));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="analytics-dashboard">
      <div className="mb-6">
        <h1 className="text-4xl font-bold text-slate-900 mb-2">Analytics</h1>
        <p className="text-lg text-slate-600">{user?.role === 'senior_executive' ? 'Your team performance overview' : 'Organisation-wide performance overview'}</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        <KpiCard icon={Users} label="Students" value={k.total_students} color="text-indigo-600" />
        <KpiCard icon={IndianRupee} label="Collected" value={`₹${k.total_collected}`} color="text-emerald-600" />
        <KpiCard icon={Target} label="Conversion" value={k.conversion_rate} suffix="%" color="text-violet-600" />
        <KpiCard icon={Brain} label="Avg SIS" value={k.avg_sis ?? '—'} color="text-fuchsia-600" />
        <KpiCard icon={Ticket} label="Open Tickets" value={k.open_tickets} color="text-amber-600" />
        <KpiCard icon={TrendingUp} label="Resolution" value={k.resolution_rate} suffix="%" color="text-sky-600" />
        <KpiCard icon={Clock} label="Avg Resolve" value={k.avg_resolution_hours ?? '—'} suffix={k.avg_resolution_hours != null ? 'h' : ''} color="text-rose-600" />
        <KpiCard icon={Star} label="Avg Rating" value={k.avg_rating ?? '—'} color="text-orange-500" />
        <KpiCard icon={Users} label="Executives" value={k.active_executives} color="text-teal-600" />
      </div>

      {/* Growth + Feedback */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="border-0 shadow-lg">
          <CardHeader><CardTitle className="flex items-center gap-2"><TrendingUp className="w-5 h-5 text-indigo-600" />Growth Trends</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={data.growth}>
                <defs>
                  <linearGradient id="gStu" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#6366f1" stopOpacity={0.4} /><stop offset="95%" stopColor="#6366f1" stopOpacity={0} /></linearGradient>
                  <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.4} /><stop offset="95%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="month" fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="students" stroke="#6366f1" fill="url(#gStu)" name="New Students" />
                <Area type="monotone" dataKey="revenue" stroke="#10b981" fill="url(#gRev)" name="Revenue (₹)" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardHeader><CardTitle className="flex items-center gap-2"><Star className="w-5 h-5 text-orange-500" />Feedback Trend</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={data.feedback.trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="month" fontSize={12} />
                <YAxis domain={[0, 5]} fontSize={12} />
                <Tooltip />
                <Line type="monotone" dataKey="avg_rating" stroke="#f59e0b" strokeWidth={3} name="Avg Rating" dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Executive Performance */}
      <Card className="border-0 shadow-lg mb-6">
        <CardHeader><CardTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-violet-600" />Executive Performance</CardTitle></CardHeader>
        <CardContent>
          {data.executive_performance.length === 0 ? (
            <p className="text-center text-slate-500 py-6">No executives yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="exec-performance-table">
                <thead>
                  <tr className="text-left text-slate-500 border-b">
                    <th className="py-2 pr-4">Executive</th>
                    <th className="py-2 px-2">Students</th>
                    <th className="py-2 px-2">Converted</th>
                    <th className="py-2 px-2">Conversion</th>
                    <th className="py-2 px-2">Follow-ups</th>
                    <th className="py-2 px-2">Tickets Resolved</th>
                    <th className="py-2 px-2">Resolution</th>
                  </tr>
                </thead>
                <tbody>
                  {data.executive_performance.map((e) => (
                    <tr key={e.executive_id} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="py-2 pr-4 font-medium text-slate-800">{e.name}</td>
                      <td className="py-2 px-2">{e.assigned_students}</td>
                      <td className="py-2 px-2">{e.converted}</td>
                      <td className="py-2 px-2"><Badge className="bg-violet-50 text-violet-700">{e.conversion_rate}%</Badge></td>
                      <td className="py-2 px-2">{e.follow_ups}</td>
                      <td className="py-2 px-2">{e.tickets_resolved}/{e.tickets_assigned}</td>
                      <td className="py-2 px-2"><Badge className="bg-sky-50 text-sky-700">{e.resolution_rate}%</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Complaints + SIS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="border-0 shadow-lg">
          <CardHeader><CardTitle className="flex items-center gap-2"><Ticket className="w-5 h-5 text-amber-600" />Complaints by Category</CardTitle></CardHeader>
          <CardContent>
            {categoryData.length === 0 ? (
              <p className="text-center text-slate-500 py-6">No tickets yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={categoryData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                    {categoryData.map((entry, i) => <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardHeader><CardTitle className="flex items-center gap-2"><Brain className="w-5 h-5 text-fuchsia-600" />SIS Score Impact</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm text-slate-500 mb-2">{data.sis.count} students analysed • avg {data.sis.avg ?? '—'}</p>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={sisData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="range" fontSize={12} />
                <YAxis fontSize={12} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#c026d3" radius={[8, 8, 0, 0]} name="Students" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Teacher Performance */}
      <Card className="border-0 shadow-lg">
        <CardHeader><CardTitle className="flex items-center gap-2"><TrendingUp className="w-5 h-5 text-emerald-600" />Teacher Performance</CardTitle></CardHeader>
        <CardContent>
          {data.teacher_performance.length === 0 ? (
            <p className="text-center text-slate-500 py-6">No teacher data yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.teacher_performance}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="teacher_name" fontSize={11} />
                <YAxis fontSize={12} />
                <Tooltip />
                <Legend />
                <Bar dataKey="completed" fill="#6366f1" name="Completed Sessions" radius={[6, 6, 0, 0]} />
                <Bar dataKey="earnings" fill="#10b981" name="Earnings (₹)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsDashboard;
